/**
 * Security Event Tracker Service
 *
 * Provides client-side security event tracking for audit and compliance.
 * Tracks user security actions, performance metrics, and error events.
 */

import { isAxiosError } from 'axios';
import { uiLogger } from '../utils/loggerInstance';

// Security event types
export type SecurityEventType =
  | 'login_attempt'
  | 'login_success'
  | 'login_failure'
  | 'logout'
  | 'session_expired'
  | 'mfa_enable'
  | 'mfa_disable'
  | 'mfa_verify_attempt'
  | 'mfa_verify_success'
  | 'mfa_verify_failure'
  | 'session_revoke'
  | 'password_change'
  | 'security_settings_access'
  | 'suspicious_activity'
  | 'rate_limit_exceeded'
  | 'api_error'
  | 'performance_issue';

// Security event severity levels
export type SecurityEventSeverity = 'low' | 'medium' | 'high' | 'critical';

// Security event interface
export interface SecurityEvent {
  type: SecurityEventType;
  severity: SecurityEventSeverity;
  success: boolean;
  timestamp: string;
  userAgent: string;
  url: string;
  referrer?: string;
  sessionId?: string;
  userId?: string;
  email?: string;
  errorMessage?: string;
  metadata?: Record<string, any>;
  performanceMetrics?: PerformanceMetrics;
}

// Performance metrics interface
export interface PerformanceMetrics {
  duration: number; // milliseconds
  networkLatency?: number;
  renderTime?: number;
  memoryUsage?: number;
  timestamp: number;
}

// Client information interface
interface ClientInfo {
  userAgent: string;
  url: string;
  referrer: string;
  timestamp: string;
  sessionId?: string;
  userId?: string;
}

class SecurityEventTracker {
  private events: SecurityEvent[] = [];
  private maxEvents = 100; // Maximum events to keep in memory
  private flushInterval = 30000; // 30 seconds
  private performanceObserver?: PerformanceObserver;

  constructor() {
    this.initializePerformanceMonitoring();
    this.startPeriodicFlush();
  }

  /**
   * Initialize performance monitoring
   */
  private initializePerformanceMonitoring(): void {
    try {
      if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
        // Check which entry types are supported
        const supportedEntryTypes = PerformanceObserver.supportedEntryTypes || [];
        const entryTypesToObserve: string[] = [];

        this.performanceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();

          for (const entry of entries) {
            // Track long tasks that might indicate performance issues
            if (entry.entryType === 'longtask' && entry.duration > 50) {
              this.trackEvent({
                type: 'performance_issue',
                severity: 'medium',
                success: false,
                errorMessage: `Long task detected: ${entry.duration}ms`,
                performanceMetrics: {
                  duration: entry.duration,
                  timestamp: entry.startTime,
                },
              });
            }

            // Track navigation timing for security-related pages
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              const isSecurityPage =
                window.location.pathname.includes('/profile') ||
                window.location.pathname.includes('/login') ||
                window.location.pathname.includes('/security');

              if (isSecurityPage) {
                this.trackEvent({
                  type: 'security_settings_access',
                  severity: 'low',
                  success: true,
                  performanceMetrics: {
                    duration: navEntry.loadEventEnd - navEntry.startTime,
                    networkLatency: navEntry.responseEnd - navEntry.requestStart,
                    renderTime: navEntry.loadEventEnd - navEntry.responseEnd,
                    timestamp: navEntry.startTime,
                  },
                });
              }
            }
          }
        });

        // Only observe supported entry types
        if (supportedEntryTypes.includes('longtask')) {
          entryTypesToObserve.push('longtask');
        }
        if (supportedEntryTypes.includes('navigation')) {
          entryTypesToObserve.push('navigation');
        }

        if (entryTypesToObserve.length > 0) {
          this.performanceObserver.observe({ entryTypes: entryTypesToObserve });
          uiLogger.debug('Security event tracker performance monitoring initialized', {
            supportedEntryTypes: entryTypesToObserve,
          });
        } else {
          uiLogger.debug('No supported performance entry types available for security monitoring');
        }
      }
    } catch (error) {
      uiLogger.warn('Failed to initialize performance monitoring', { error });
    }
  }

  /**
   * Start periodic flush of events to server
   */
  private startPeriodicFlush(): void {
    setInterval(() => {
      this.flushEvents();
    }, this.flushInterval);
  }

  /**
   * Get client information for event tracking
   */
  private getClientInfo(): ClientInfo {
    const sessionId = localStorage.getItem('session_id') || sessionStorage.getItem('session_id');
    const userId = this.getCurrentUserId();

    return {
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      sessionId: sessionId || undefined,
      userId: userId || undefined,
    };
  }

  /**
   * Get current user ID from stored auth data
   */
  private getCurrentUserId(): string | null {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || null;
      }
    } catch (error) {
      uiLogger.debug('Failed to get current user ID', { error });
    }
    return null;
  }

  /**
   * Track a security event
   */
  public trackEvent(eventData: Partial<SecurityEvent>): void {
    try {
      const clientInfo = this.getClientInfo();

      const event: SecurityEvent = {
        type: eventData.type || 'api_error',
        severity: eventData.severity || 'low',
        success: eventData.success ?? true,
        timestamp: clientInfo.timestamp,
        userAgent: clientInfo.userAgent,
        url: clientInfo.url,
        referrer: clientInfo.referrer,
        sessionId: eventData.sessionId || clientInfo.sessionId,
        userId: eventData.userId || clientInfo.userId,
        errorMessage: eventData.errorMessage,
        metadata: eventData.metadata,
        performanceMetrics: eventData.performanceMetrics,
      };

      // Add to local events array
      this.events.push(event);

      // Keep only the most recent events
      if (this.events.length > this.maxEvents) {
        this.events = this.events.slice(-this.maxEvents);
      }

      // Log locally for debugging
      uiLogger.debug('Security event tracked', {
        type: event.type,
        severity: event.severity,
        success: event.success,
        url: event.url,
      });

      // For critical events, flush immediately
      if (event.severity === 'critical' || event.type === 'suspicious_activity') {
        this.flushEvents();
      }
    } catch (error) {
      uiLogger.error('Failed to track security event', { error, eventData });
    }
  }

  /**
   * Track authentication events
   */
  public trackAuthEvent(
    type: 'login_attempt' | 'login_success' | 'login_failure' | 'logout',
    success: boolean,
    email?: string,
    errorMessage?: string,
    metadata?: Record<string, any>,
  ): void {
    const severity: SecurityEventSeverity = success
      ? 'low'
      : type === 'login_failure'
        ? 'medium'
        : 'low';

    this.trackEvent({
      type,
      severity,
      success,
      email,
      errorMessage,
      metadata: {
        ...metadata,
        authMethod: 'email_password',
      },
    });
  }

  /**
   * Track MFA events
   */
  public trackMFAEvent(
    type:
      | 'mfa_enable'
      | 'mfa_disable'
      | 'mfa_verify_attempt'
      | 'mfa_verify_success'
      | 'mfa_verify_failure',
    success: boolean,
    errorMessage?: string,
    metadata?: Record<string, any>,
  ): void {
    const severity: SecurityEventSeverity = success ? 'low' : 'medium';

    this.trackEvent({
      type,
      severity,
      success,
      errorMessage,
      metadata: {
        ...metadata,
        mfaMethod: 'email',
      },
    });
  }

  /**
   * Track session management events
   */
  public trackSessionEvent(
    type: 'session_revoke' | 'session_expired',
    success: boolean,
    sessionId?: string,
    errorMessage?: string,
    metadata?: Record<string, any>,
  ): void {
    this.trackEvent({
      type,
      severity: 'medium',
      success,
      sessionId,
      errorMessage,
      metadata,
    });
  }

  /**
   * Track API errors with security implications
   */
  public trackAPIError(
    error: Error | unknown,
    endpoint: string,
    method: string,
    metadata?: Record<string, any>,
  ): void {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const isSecurityError = status === 401 || status === 403 || status === 429;

    let severity: SecurityEventSeverity = 'low';
    let eventType: SecurityEventType = 'api_error';

    if (status === 429) {
      severity = 'medium';
      eventType = 'rate_limit_exceeded';
    } else if (status === 401 || status === 403) {
      severity = 'medium';
      eventType = 'suspicious_activity';
    }

    const errorMessage =
      error instanceof Error ? error.message : `HTTP ${status ?? 'unknown'} error`;

    const cfgMeta =
      isAxiosError(error) && error.config && typeof error.config === 'object'
        ? (error.config as { metadata?: { endTime?: number; startTime?: number } }).metadata
        : undefined;

    this.trackEvent({
      type: eventType,
      severity,
      success: false,
      errorMessage,
      metadata: {
        ...metadata,
        endpoint,
        method,
        status,
        isSecurityError,
        responseTime:
          cfgMeta?.endTime != null && cfgMeta?.startTime != null
            ? cfgMeta.endTime - cfgMeta.startTime
            : undefined,
      },
    });
  }

  /**
   * Track performance metrics for security operations
   */
  public trackPerformance(
    operation: string,
    startTime: number,
    success: boolean,
    metadata?: Record<string, any>,
  ): void {
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Consider operations over 5 seconds as performance issues
    const isSlowOperation = duration > 5000;

    this.trackEvent({
      type: isSlowOperation ? 'performance_issue' : 'security_settings_access',
      severity: isSlowOperation ? 'medium' : 'low',
      success,
      performanceMetrics: {
        duration,
        timestamp: endTime,
      },
      metadata: {
        ...metadata,
        operation,
        isSlowOperation,
      },
    });
  }

  /**
   * Flush events to server
   */
  public async flushEvents(): Promise<void> {
    if (this.events.length === 0) {
      return;
    }

    const eventsToFlush = [...this.events];
    this.events = []; // Clear local events

    try {
      // Send events to server (if endpoint exists)
      // For now, we'll just log them locally
      uiLogger.info('Flushing security events', {
        eventCount: eventsToFlush.length,
        events: eventsToFlush.map((e) => ({
          type: e.type,
          severity: e.severity,
          success: e.success,
          timestamp: e.timestamp,
        })),
      });

      // TODO: Implement server endpoint for security event collection
      // Tracking issue: https://github.com/org/repo/issues/1234 (see for progress/timeline)
      // await api.post('/api/v1/security/events', { events: eventsToFlush });
    } catch (error) {
      uiLogger.error('Failed to flush security events', { error });

      // Re-add events back to queue on failure (up to max limit)
      this.events = [...eventsToFlush.slice(-this.maxEvents / 2), ...this.events];
    }
  }

  /**
   * Get current events (for debugging)
   */
  public getEvents(): SecurityEvent[] {
    return [...this.events];
  }

  /**
   * Clear all events
   */
  public clearEvents(): void {
    this.events = [];
    uiLogger.debug('Security events cleared');
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }
    this.flushEvents();
  }
}

// Create singleton instance
export const securityEventTracker = new SecurityEventTracker();

// Export for use in other modules
export default securityEventTracker;
